export class FormService {
    static buildDTO(formData) {
        return {
            IdContrato: formData.contrato,
            NomeEntidadeOuOrgao: (formData.entidade || '').trim(),
            AtualizadoEm: FormService.toIsoDate(formData.atualizado),
            Contratada: (formData.contratada || '').trim(),
            CnpjContratada: FormService.onlyDigits(formData.cnpj),
            Objeto: (formData.objeto || '').trim(),
            NumeroProcessoLicitatorio: (
                formData.numeroProcessoLicitatorio || ''
            ).trim(),
            ModalidadeLicitacao: (formData.modalidadeLicitacao || '').trim(),
            NumeroContrato: formData.numeroDoContrato || '',
            AnoContrato:
                formData.anoDoContrato != null
                    ? String(formData.anoDoContrato)
                    : null,
            VigenciaInicio: FormService.toIsoDate(formData.inicioDaVigencia),
            VigenciaFim: FormService.toIsoDate(formData.fimDaVigencia),
            AditivoPrazo: FormService.formatWithOrdinal(formData.aditivoDePrazo),
            NumeroApostilamento: FormService.formatWithOrdinal(formData.numeroDoApostilamento),
            AditivoValor: FormService.formatWithOrdinal(formData.aditivoDeValor),
            ValorTotalContrato: FormService.toDecimalOrNull(
                formData.valorTotal
            ),
            ValorMensal: FormService.toDecimalOrNull(
                formData.valorMensal
            ),
            ValorExecutado: FormService.toDecimalOrNull(
                formData.valorExecutado
            ),
            NomeFiscalContrato: (formData.nomeFiscal || '').trim(),
            Situacao: formData.situacao,
            Justificativa: (formData.justificativa || '').trim()
        }
    }

    static toIsoDate(dateString) {
        if (!dateString) return null
        const datePart = String(dateString).split(' ')[0]
        const [dd, mm, yyyy] = datePart.split('/')
        if (!dd || !mm || !yyyy) return null

        const timePart = String(dateString).split(' ')[1]
        if (timePart) {
            const [hh, mmTime] = timePart.split(':')
            return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T${hh.padStart(2, '0')}:${(mmTime || '00').padStart(2, '0')}:00`
        }

        return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`
    }

    static parseDate(dateString) {
        if (!dateString) return null
        const [dd, mm, yyyy] = String(dateString).split('/')
        if (!dd || !mm || !yyyy) return null
        return new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd))
    }

    static onlyDigits(string) {
        return (string || '').replace(/\D+/g, '')
    }

    static toDecimalOrNull(value) {
        if (value === '' || value == null) return null
        if (typeof value === 'number') return value
        const num = String(value)
            .replace(/[^\d,.-]/g, '')
            .replace(/\./g, '')
            .replace(',', '.')
        const parsed = Number(num)
        return Number.isFinite(parsed) ? parsed : null
    }

    static startBeforeOrEqualEnd(startDate, endDate) {
        if (!startDate || !endDate) return true
        const start = this.parseDate(startDate)
        const end = this.parseDate(endDate)
        if (!start || !end) return true
        return start <= end
    }

    static formatWithOrdinal(value) {
        if (!value || value === '' || value == null) {
            return ''
        }
        const trimmed = String(value).trim()
        if (trimmed === '') {
            return ''
        }
        return `${trimmed}º`
    }
}

export class ContractService {
    static async getContracts(gestaoJuridicaServicos) {
        return gestaoJuridicaServicos.obterContratosVigentes()
    }

    static async getContractById(gestaoJuridicaServicos, contractId) {
        return gestaoJuridicaServicos.obterContratosPorId(contractId)
    }

    static fillContractData(contractData, formData) {
        formData.inicioDaVigencia = ContractService.formatDate(
            contractData.dtInicioVigencia
        )
        formData.fimDaVigencia = ContractService.formatDate(
            contractData.dtFimVigencia
        )
        formData.contratada = contractData.fornecedor.nome
        formData.cnpj = contractData.fornecedor.cnpj
        formData.objeto = contractData.vcObjetoContrato
        formData.modalidadeLicitacao = contractData.tipoLicitacao
        formData.valorTotal = contractData.nuValorGlobalContrato
        formData.valorExecutado = contractData.nuValorGlobalContrato - contractData.nuSaldoContratoReal
        formData.nomeFiscal = contractData.nomeFiscal
        formData.numeroDoContrato = ContractService.formatContractNumber(
            contractData.vcCodigoEstruturado
        )
        formData.anoDoContrato = ContractService.extractYearFromCode(
            contractData.vcCodigoEstruturado,
            contractData.dtInicioVigencia
        )
        formData.situacao = ContractService.determinarSituacao(
            contractData.dtInicioVigencia,
            contractData.dtFimVigencia
        )
        formData.entidade = contractData.nomeUnidade

        formData.valorMensal = ContractService.calcularValorMensal(
            contractData.comprometimentos
        )
    }

    static calcularValorMensal(comprometimentos) {
        if (!comprometimentos || !Array.isArray(comprometimentos) || comprometimentos.length === 0) {
            return 0
        }

        const moment = require('moment')
        const agora = moment()
        const mesAtual = agora.month()
        const anoAtual = agora.year()

        const valorMensal = comprometimentos
            .filter(comp => {
                if (!comp.idRm || comp.idRm === '' || comp.idRm == null) {
                    return false
                }

                const dataEmissao = comp.dtEmissao || comp.dataEmissao
                if (!dataEmissao) {
                    return false
                }

                const data = moment(dataEmissao)
                return data.month() === mesAtual && data.year() === anoAtual
            })
            .reduce((soma, comp) => {
                const valor = comp.nuValorComprometimento || comp.valor || 0
                return soma + (Number(valor) || 0)
            }, 0)
        return valorMensal
    }

    static formatDate(date) {
        if (!date) return null
        const moment = require('moment')
        return moment(date).format('DD/MM/YYYY')
    }

    static formatYear(date) {
        if (!date) return null
        const moment = require('moment')
        return moment(date).format('YYYY')
    }

    static extractYearFromCode(vcCodigoEstruturado, dtInicioVigencia) {
        if (!vcCodigoEstruturado) {
            return ContractService.formatYear(dtInicioVigencia)
        }

        const codigoStr = String(vcCodigoEstruturado)
        const primeiraBarraIndex = codigoStr.indexOf('/')

        if (primeiraBarraIndex === -1) {
            return ContractService.formatYear(dtInicioVigencia)
        }

        const parteDepoisBarra = codigoStr.substring(primeiraBarraIndex + 1)

        if (parteDepoisBarra.length < 4) {
            return ContractService.formatYear(dtInicioVigencia)
        }

        const quatroPrimeirosDigitos = parteDepoisBarra.substring(0, 4)
        const anoExtraido = parseInt(quatroPrimeirosDigitos, 10)

        if (isNaN(anoExtraido) || anoExtraido < 1900) {
            return ContractService.formatYear(dtInicioVigencia)
        }

        return String(anoExtraido)
    }

    static formatContractNumber(numeroOrdem) {
        if (!numeroOrdem) return null

        const numeroOrdemStr = String(numeroOrdem)
        const primeiraBarraIndex = numeroOrdemStr.indexOf('/')

        if (primeiraBarraIndex === -1) {
            return numeroOrdemStr
        }

        const parteAntes = numeroOrdemStr.substring(0, primeiraBarraIndex)
        const parteDepois = numeroOrdemStr.substring(primeiraBarraIndex + 1)

        if (parteDepois.length <= 4) {
            return parteAntes
        }

        const parteDepoisSem4Digitos = parteDepois.substring(4)

        return parteDepoisSem4Digitos !== '/' ? `${parteAntes}${parteDepoisSem4Digitos}` : parteAntes
    }

    static determinarSituacao(dtInicioVigencia, dtFimVigencia) {
        if (!dtInicioVigencia || !dtFimVigencia) {
            return 'EM EXECUÇÃO'
        }

        const moment = require('moment')
        const dataAtual = moment()
        const inicioVigencia = moment(dtInicioVigencia)
        const fimVigencia = moment(dtFimVigencia)

        if (dataAtual.isAfter(fimVigencia, 'day')) {
            return 'ENCERRADO'
        }

        if (dataAtual.isSameOrAfter(inicioVigencia, 'day') && dataAtual.isSameOrBefore(fimVigencia, 'day')) {
            return 'EM EXECUÇÃO'
        }

        return 'EM EXECUÇÃO'
    }
}
